// ── CEC 2024 Rule 8-200 residential load calculation — the shared engine ─────
//
// Lifted out of src/components/CECLoadCalc.jsx so the SAME arithmetic answers
// three callers: the calculator UI, the saved-calc API, and the AI tools in the
// assessment / quote chats. A second copy of this maths is how a quote and a
// permit drawing end up disagreeing, so there is exactly one.
//
// Dependency-free ESM on purpose — required straight from CommonJS API handlers
// (api/_loadCalcChatTools.js, by a STATIC path so Vercel ships this file with
// the function; dynamic import is the fallback on a runtime that cannot
// require ESM) and from scripts/test_cec_load_calc.js.
//
// ── TWO KINDS OF SHEDDING, AND THEY ARE NOT THE SAME QUESTION ────────────────
// `shed`  — an energy management system drops this load so it does not count
//           against the SERVICE. This is the CEC question: it changes the
//           calculated demand the service size is judged against.
// `gen`   — what happens to this load on GENERATOR backup: "backup" (the genny
//           carries it), "shed" (an SMM drops it when the genny is loaded) or
//           "off" (not on the backed-up panel at all).
//
// A hot tub is the ordinary case for why these must be separate: it counts in
// full against the service (shed = false) and is the first thing dropped on the
// generator (gen = "shed"). Collapsing them into one flag makes that
// unrepresentable, and every generator in the shop is sized off it.

import { pickGenerator, normalizeFuel } from "./generatorSpecs.js";

export const SQ_FT_TO_SQ_M = 0.092903;
export const VOLTAGE = 240;

// ── Climate loads ────────────────────────────────────────────────────────────
// Heating and A/C used to be two scalars — one bulked `heatingWatts` and one
// bulked `acWatts`. That forced the tech to add a heat pump, a backup furnace
// and a garage heater into a single number and then explain the breakdown to
// the AI in prose. Now each one is its own labelled row, and the CEC demand
// factor applies to the SUM exactly as it did to the bulked figure — Rule
// 8-200(1)(a)(iii) factors the total space-heating load, not each appliance.
//
// `side` decides which half of the 8-200(3) heating-vs-cooling comparison the
// row lands in. `type` is purely a label — it does not change the arithmetic;
// it exists so "which of these is the heat pump?" is answerable without asking
// the tech, which is what load shedding decisions turn on.
export const CLIMATE_TYPES = [
  { key: "heat_pump",   label: "Heat pump",            side: "heat" },
  { key: "furnace",     label: "Electric furnace",     side: "heat" },
  { key: "baseboard",   label: "Baseboard heat",       side: "heat" },
  { key: "boiler",      label: "Electric boiler",      side: "heat" },
  { key: "in_floor",    label: "In-floor heat",        side: "heat" },
  { key: "unit_heater", label: "Unit / garage heater", side: "heat" },
  { key: "air_handler", label: "Air handler / strip",  side: "heat" },
  { key: "other_heat",  label: "Other heating",        side: "heat" },
  { key: "central_ac",  label: "Central A/C",          side: "ac" },
  { key: "mini_split",  label: "Mini-split",           side: "ac" },
  { key: "other_ac",    label: "Other cooling",        side: "ac" },
];

export const climateTypeLabel = (key) =>
  CLIMATE_TYPES.find((t) => t.key === key)?.label || "Climate load";

export const climateTypeSide = (key) =>
  CLIMATE_TYPES.find((t) => t.key === key)?.side || "heat";

// ── Generator modes ──────────────────────────────────────────────────────────
export const GEN_MODES = [
  { key: "backup", label: "On backup",   hint: "the generator carries this load" },
  { key: "shed",   label: "Shed (SMM)",  hint: "a load-shedding module drops it when the generator is loaded" },
  { key: "off",    label: "Not backed up", hint: "not on the generator at all" },
];

export const normalizeGenMode = (v) =>
  v === "shed" || v === "off" || v === "backup" ? v : "backup";

// ── Legacy migration ─────────────────────────────────────────────────────────
// Saved calcs predate climateItems and carry heatingWatts / acWatts scalars.
// They must reload with byte-identical results, so a legacy calc becomes
// exactly one heating row and one A/C row with the same watts and shed flags.
// Only ever widens: an input that already has climateItems is returned as-is.
export function normalizeClimateItems(inputs = {}) {
  if (Array.isArray(inputs.climateItems)) {
    return inputs.climateItems.map((it, i) => ({
      id: it.id || `climate_${i + 1}`,
      type: it.type || "other_heat",
      side: it.side || climateTypeSide(it.type),
      label: it.label || climateTypeLabel(it.type),
      watts: Number(it.watts) || 0,
      circuit: it.circuit || "",
      enabled: it.enabled !== false,
      shed: !!it.shed,
      gen: normalizeGenMode(it.gen),
    }));
  }

  const out = [];
  if (inputs.heatingEnabled && Number(inputs.heatingWatts) > 0) {
    out.push({
      id: "heat_legacy", type: "other_heat", side: "heat",
      // Unlabelled on purpose: a migrated row is a bulked total, not a known
      // appliance. Calling it "Heat pump" would invent a fact.
      label: "Electric space heating",
      watts: Number(inputs.heatingWatts) || 0,
      circuit: "", enabled: true, shed: !!inputs.heatingShed, gen: "backup",
    });
  }
  if (inputs.acEnabled && Number(inputs.acWatts) > 0) {
    out.push({
      id: "ac_legacy", type: "other_ac", side: "ac",
      label: "Air conditioning",
      watts: Number(inputs.acWatts) || 0,
      circuit: "", enabled: true, shed: !!inputs.acShed, gen: "backup",
    });
  }
  return out;
}

/**
 * Rule 8-200(1)(a)(iii) + 8-200(3) over a list of labelled climate rows.
 *
 * Heating: the demand factor applies to the SUM — first 10 kW at 100 %, the
 * remainder at 75 %. Applying it per-appliance would under-count badly (three
 * 6 kW heaters would come out 18 kW instead of 16 kW), which is precisely why
 * the rows are summed before the factor and not after.
 *
 * Cooling: 100 %, no factor.
 *
 * Then the interlock: unless the tech says heating and cooling can run at the
 * same time, only the LARGER of the two counts.
 */
export function climateDemand(items, { heatAcSimultaneous = false } = {}) {
  const live = (items || []).filter((it) => it.enabled !== false && Number(it.watts) > 0);
  const shedRows = live.filter((it) => it.shed);
  const counted = live.filter((it) => !it.shed);

  const heatRows = counted.filter((it) => (it.side || climateTypeSide(it.type)) === "heat");
  const acRows = counted.filter((it) => (it.side || climateTypeSide(it.type)) === "ac");

  const heatingConnected = heatRows.reduce((s, it) => s + (Number(it.watts) || 0), 0);
  const acConnected = acRows.reduce((s, it) => s + (Number(it.watts) || 0), 0);

  // Nameplate INCLUDES shed rows. The old scalar engine reported
  // `heatingWatts: heatingEnabled ? heatingWatts : 0` — the installed load,
  // whether or not an EMS drops it — and only zeroed the DEMAND. The PDF and
  // the Jobber note both print it, so a shed heater must still show its
  // nameplate (verified against Mike Crawford's saved calc: 7200 W shed,
  // heatingWatts 7200, heatingDemand 0).
  const sumAll = (side) => live
    .filter((it) => (it.side || climateTypeSide(it.type)) === side)
    .reduce((s, it) => s + (Number(it.watts) || 0), 0);
  const heatingNameplate = sumAll("heat");
  const acNameplate = sumAll("ac");

  const heatingDemand = heatingConnected <= 10000
    ? heatingConnected
    : 10000 + 0.75 * (heatingConnected - 10000);
  const acDemand = acConnected;

  const bothPresent = heatingDemand > 0 && acDemand > 0;
  let heatingOrAC, heatAcMode;
  if (bothPresent && heatAcSimultaneous) {
    heatingOrAC = heatingDemand + acDemand;
    heatAcMode = "both";
  } else if (heatingDemand > 0 || acDemand > 0) {
    heatingOrAC = Math.max(heatingDemand, acDemand);
    heatAcMode = heatingDemand >= acDemand ? "heating" : "ac";
  } else {
    heatingOrAC = 0;
    heatAcMode = "none";
  }

  return {
    heatingConnected,
    acConnected,
    heatingNameplate,
    acNameplate,
    heatingDemand: Math.round(heatingDemand),
    acDemand: Math.round(acDemand),
    heatingOrAC: Math.round(heatingOrAC),
    heatAcMode,
    usingHeating: heatAcMode === "heating",
    heatAcSimultaneous: !!(bothPresent && heatAcSimultaneous),
    heatRows,
    acRows,
    shedRows,
  };
}

export function additionalDemandFor(additionalRawTotal, rangeEnabled) {
  if (!additionalRawTotal) {
    return {
      additionalDemand: 0,
      additionalRuleBranch: rangeEnabled ? "with_range" : "without_range",
      additionalFirstBlock: 0,
      additionalRemainder: 0,
    };
  }
  if (rangeEnabled) {
    return {
      additionalDemand: Math.round(additionalRawTotal * 0.25),
      additionalRuleBranch: "with_range",
      additionalFirstBlock: 0,
      additionalRemainder: additionalRawTotal,
    };
  }
  const additionalFirstBlock = Math.min(additionalRawTotal, 6000);
  const additionalRemainder = Math.max(0, additionalRawTotal - 6000);
  return {
    additionalDemand: Math.round(additionalFirstBlock + additionalRemainder * 0.25),
    additionalRuleBranch: "without_range",
    additionalFirstBlock,
    additionalRemainder,
  };
}

/**
 * CEC 2024 Rule 8-200(1)(a) — the full residential demand calculation, in
 * code-book order. Shape of the return value is unchanged from the version
 * that lived in the component (the PDF, the Jobber note and the saved
 * `result` JSONB all read these keys), with the climate rows added alongside
 * the old scalar heating/AC keys rather than replacing them.
 */
export function calculateCECDemand(inputs = {}) {
  const {
    aboveGradeSqFt, basementSqFt,
    heatAcSimultaneous,
    rangeEnabled, rangeNameplateW, rangeShed,
    waterHeatingItems = [],
    evEnabled, evWatts, evems,
    additionalItems = [],
    newLoadWatts, newLoadShed, newLoadLabel,
  } = inputs;

  const climateItems = normalizeClimateItems(inputs);
  const shedLoads = [];

  // 1. Base load — Rule 8-110 / 8-200(1)(a)(i)-(ii)
  const aboveM2 = (Number(aboveGradeSqFt) || 0) * SQ_FT_TO_SQ_M;
  const basementM2 = (Number(basementSqFt) || 0) * SQ_FT_TO_SQ_M * 0.75;
  const totalM2 = aboveM2 + basementM2;
  const baseLoad = 5000 + Math.max(0, Math.ceil((totalM2 - 90) / 90)) * 1000;

  // 2 + 3. Climate — Rule 8-200(1)(a)(iii), interlocked per 8-200(3)
  const climate = climateDemand(climateItems, { heatAcSimultaneous });
  for (const it of climate.shedRows) {
    shedLoads.push({ key: it.id, label: it.label, watts: it.watts, circuit: it.circuit || null });
  }

  // 4. Electric range — Rule 8-200(1)(a)(iv). A shed range still counts as
  // "range provided" for the additional-load branch: it is installed, the EMS
  // merely drops it.
  let rangeCalculated = 0;
  const rangeNP = Number(rangeNameplateW) || 12000;
  if (rangeEnabled && rangeShed) {
    shedLoads.push({ key: "range", label: "Electric range", watts: rangeNP, circuit: inputs.rangeCircuit || null });
  } else if (rangeEnabled) {
    rangeCalculated = rangeNP <= 12000 ? 6000 : 6000 + 0.40 * (rangeNP - 12000);
  }

  // 5. Water heating / hot tubs / pool heaters — Rule 8-200(1)(a)(v)
  let waterHeatingTotal = 0;
  for (const item of waterHeatingItems) {
    if (!item.enabled) continue;
    if (item.shed) {
      shedLoads.push({ key: item.id, label: item.label || "Water heating load", watts: item.watts || 0, circuit: item.circuit || null });
    } else {
      waterHeatingTotal += item.watts || 0;
    }
  }

  // 6. EV charging — Rule 8-200(1)(a)(vi). EVEMS sheds the charger entirely.
  const evDemand = evEnabled ? (evems ? 0 : (Number(evWatts) || 0)) : 0;
  if (evEnabled && evems && Number(evWatts) > 0) {
    shedLoads.push({ key: "ev", label: "EV charger (EVEMS)", watts: Number(evWatts), circuit: inputs.evCircuit || null });
  }

  // 7. Additional loads >1500 W — Rule 8-200(1)(a)(vii)
  for (const item of additionalItems) {
    if (item.shed) {
      shedLoads.push({ key: item.id, label: item.label || "Additional load", watts: item.watts || 0, circuit: item.circuit || null });
    }
  }
  const additionalRawTotal = additionalItems.reduce(
    (sum, item) => sum + (item.shed ? 0 : (item.watts || 0)), 0
  );
  const { additionalDemand, additionalRuleBranch, additionalFirstBlock, additionalRemainder } =
    additionalDemandFor(additionalRawTotal, rangeEnabled);

  // New load under test — sheddable like any other.
  let newLoadDemand = Number(newLoadWatts) || 0;
  if (newLoadDemand > 0 && newLoadShed) {
    shedLoads.push({ key: "newLoad", label: `New load${newLoadLabel ? ` (${newLoadLabel})` : ""}`, watts: newLoadDemand, circuit: inputs.newLoadCircuit || null });
    newLoadDemand = 0;
  }

  const totalDemand =
    baseLoad + climate.heatingOrAC + Math.round(rangeCalculated) +
    waterHeatingTotal + evDemand + additionalDemand + newLoadDemand;

  return {
    aboveM2: Math.round(aboveM2 * 100) / 100,
    basementM2Raw: Math.round(((Number(basementSqFt) || 0) * SQ_FT_TO_SQ_M) * 100) / 100,
    basementM2Adjusted: Math.round(basementM2 * 100) / 100,
    totalM2: Math.round(totalM2 * 100) / 100,
    baseLoad,

    // Climate — per-row detail plus the legacy scalar keys the PDF/note read.
    climateItems,
    climateRows: climate.heatRows.concat(climate.acRows),
    heatingWatts: climate.heatingNameplate,
    heatingDemand: climate.heatingDemand,
    heatingShed: climate.shedRows.some((r) => (r.side || climateTypeSide(r.type)) === "heat"),
    acWatts: climate.acNameplate,
    acDemand: climate.acDemand,
    acShed: climate.shedRows.some((r) => (r.side || climateTypeSide(r.type)) === "ac"),
    heatingOrAC: climate.heatingOrAC,
    usingHeating: climate.usingHeating,
    heatAcMode: climate.heatAcMode,
    heatAcSimultaneous: climate.heatAcSimultaneous,

    rangeEnabled: !!rangeEnabled,
    rangeShed: !!(rangeEnabled && rangeShed),
    rangeNameplate: rangeNP,
    rangeCalculated: Math.round(rangeCalculated),

    waterHeatingTotal,
    evDemand,

    additionalRawTotal,
    additionalDemand,
    additionalRuleBranch,
    additionalFirstBlock,
    additionalRemainder,

    newLoadWatts: Number(newLoadWatts) || 0,
    newLoadDemand,
    newLoadShed: !!(newLoadShed && (Number(newLoadWatts) || 0) > 0),
    newLoadLabel: newLoadLabel || "",

    shedLoads,
    shedWattsTotal: shedLoads.reduce((sum, s) => sum + (s.watts || 0), 0),

    totalDemand: Math.round(totalDemand),
    demandAmps: Math.round((totalDemand / VOLTAGE) * 10) / 10,
  };
}

// ── Generator scenario ───────────────────────────────────────────────────────
// Every load in the calc carries a `gen` mode. Rather than write a second
// demand calculation for the generator — which would drift from the CEC one
// within a month — project the inputs into a generator VIEW (drop what is not
// backed up, mark what an SMM sheds) and run the very same engine over it.

/** Every individually-addressable load in a calc, flattened, with its gen mode. */
export function enumerateLoads(inputs = {}) {
  const out = [];
  for (const it of normalizeClimateItems(inputs)) {
    if (it.enabled === false || !(Number(it.watts) > 0)) continue;
    out.push({ group: "climate", id: it.id, label: it.label, type: it.type,
      watts: Number(it.watts), circuit: it.circuit || null, gen: normalizeGenMode(it.gen) });
  }
  if (inputs.rangeEnabled) {
    out.push({ group: "range", id: "range", label: "Electric range", type: "range",
      watts: Number(inputs.rangeNameplateW) || 12000, circuit: inputs.rangeCircuit || null,
      gen: normalizeGenMode(inputs.rangeGen) });
  }
  for (const it of inputs.waterHeatingItems || []) {
    if (!it.enabled || !(Number(it.watts) > 0)) continue;
    out.push({ group: "water_heating", id: it.id, label: it.label || "Water heating",
      type: "water_heating", watts: Number(it.watts), circuit: it.circuit || null,
      gen: normalizeGenMode(it.gen) });
  }
  if (inputs.evEnabled && Number(inputs.evWatts) > 0) {
    out.push({ group: "ev", id: "ev", label: "EV charger", type: "ev",
      watts: Number(inputs.evWatts), circuit: inputs.evCircuit || null,
      gen: normalizeGenMode(inputs.evGen) });
  }
  for (const it of inputs.additionalItems || []) {
    if (!(Number(it.watts) > 0)) continue;
    out.push({ group: "additional", id: it.id, label: it.label || "Additional load",
      type: it.type || "additional", watts: Number(it.watts), circuit: it.circuit || null,
      gen: normalizeGenMode(it.gen) });
  }
  return out;
}

/** Inputs as the generator sees them: "off" removed, "shed" marked shed. */
export function projectForGenerator(inputs = {}) {
  const mode = (it, fallback) => normalizeGenMode(it?.gen ?? fallback);
  const project = (it, fallback) => {
    const m = mode(it, fallback);
    // "off" is expressed as disabled rather than deleted so the row still
    // round-trips — the calculator has to be able to show what it excluded.
    if (m === "off") return { ...it, enabled: false, shed: false };
    if (m === "shed") return { ...it, shed: true };
    return { ...it, shed: false };
  };

  const rangeMode = normalizeGenMode(inputs.rangeGen);
  const evMode = normalizeGenMode(inputs.evGen);

  return {
    ...inputs,
    climateItems: normalizeClimateItems(inputs).map((it) => project(it)),
    waterHeatingItems: (inputs.waterHeatingItems || []).map((it) => project(it)),
    additionalItems: (inputs.additionalItems || []).map((it) => project(it)),
    rangeEnabled: rangeMode === "off" ? false : !!inputs.rangeEnabled,
    rangeShed: rangeMode === "shed",
    evEnabled: evMode === "off" ? false : !!inputs.evEnabled,
    evems: evMode === "shed",
    // The "new load" row is a what-if against the SERVICE, not something the
    // generator is being asked to carry. Excluded unless explicitly backed up.
    newLoadWatts: normalizeGenMode(inputs.newLoadGen) === "backup" ? inputs.newLoadWatts : 0,
  };
}

/**
 * What the generator actually has to carry, and which unit covers it.
 *
 * @param inputs               a saved calc's `inputs` object
 * @param fuel                 "ng" | "lp" — required; the ratings differ
 * @param spareCapacityPct     opt-in headroom on top of calculated demand
 * @param overrides            { [loadId]: "backup"|"shed"|"off" } — what-if
 *                             without mutating the saved calc
 */
export function generatorScenario(inputs = {}, { fuel, spareCapacityPct = 0, overrides = null } = {}) {
  let src = inputs;
  if (overrides && Object.keys(overrides).length) {
    const apply = (it) => (overrides[it.id] ? { ...it, gen: normalizeGenMode(overrides[it.id]) } : it);
    src = {
      ...inputs,
      climateItems: normalizeClimateItems(inputs).map(apply),
      waterHeatingItems: (inputs.waterHeatingItems || []).map(apply),
      additionalItems: (inputs.additionalItems || []).map(apply),
      rangeGen: overrides.range || inputs.rangeGen,
      evGen: overrides.ev || inputs.evGen,
    };
  }

  const loads = enumerateLoads(src);
  const serviceResult = calculateCECDemand(src);
  const genResult = calculateCECDemand(projectForGenerator(src));

  const backed = loads.filter((l) => l.gen === "backup");
  const shed = loads.filter((l) => l.gen === "shed");
  const off = loads.filter((l) => l.gen === "off");

  const sizing = normalizeFuel(fuel)
    ? pickGenerator({ demandAmps: genResult.demandAmps, fuel, spareCapacityPct })
    : { error: "fuel must be 'ng' or 'lp' — the NG and LP ratings differ on every unit except the 14 kW" };

  return {
    service: {
      demand_watts: serviceResult.totalDemand,
      demand_amps: serviceResult.demandAmps,
      demand_kw: Math.round(serviceResult.totalDemand / 100) / 10,
    },
    generator: {
      demand_watts: genResult.totalDemand,
      demand_amps: genResult.demandAmps,
      demand_kw: Math.round(genResult.totalDemand / 100) / 10,
    },
    // What the shedding actually bought you — the number the tech is after when
    // they ask "what if I shed the hot tub and the range?".
    saved_watts: serviceResult.totalDemand - genResult.totalDemand,
    saved_amps: Math.round((serviceResult.demandAmps - genResult.demandAmps) * 10) / 10,
    loads: { backed_up: backed, shed, not_backed_up: off },
    // One SMM per shed load; 50 A modules up to 50 A, 100 A above that.
    smm_count: shed.length,
    smms: shed.map((l) => {
      const amps = Math.round((l.watts / VOLTAGE) * 10) / 10;
      return { id: l.id, label: l.label, circuit: l.circuit, watts: l.watts, amps,
        module: amps <= 50 ? "SMM_50A" : "SMM_100A" };
    }),
    sizing,
    breakdown: genResult,
  };
}
